import React, { useState, ReactNode } from 'react';
import toast from 'react-hot-toast';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setModalName } from '@/lib/slices/modal-slice';

import ModalComponent from '@/common/components/modals/ModalComponent';
import Button from '@/common/components/ui/Button';
import { CancelIcon, CopyIcon } from '@/assets/icons/button';
import LoadingSpinner from '../../LoadingSpinner';

interface InfoModalProps {
  modalName: string;
  title: string;
  subtitle?: string;
  children: ReactNode;
  primaryAction?: {
    text: string;
    icon?: ReactNode;
    onClick: () => Promise<void> | void;
    disabled?: boolean;
  };
  secondaryAction?: {
    text: string;
    icon?: ReactNode;
    onClick: () => Promise<void> | void;
    disabled?: boolean;
  };
  rightAction?: {
    text: string;
    icon?: ReactNode;
    onClick: () => Promise<void> | void;
    disabled?: boolean;
  };
  copyFeature?: {
    text: string;
    enabled?: boolean;
  };
  className?: string;
  closeOnClickOff?: boolean;
  loadingText?: string;
  bodyClassName?: string;
  hideClose?: boolean;
}

export default function InfoModal({
  modalName,
  title,
  subtitle,
  children,
  primaryAction,
  secondaryAction,
  rightAction,
  copyFeature,
  className = 'max-w-[500px] w-full',
  closeOnClickOff = true,
  loadingText = 'Processing...',
  bodyClassName = 'bg-surface_gray_200',
  hideClose = false,
}: InfoModalProps) {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const modalVisible: boolean = useAppSelector(() => store.getState().modal.visible);
  const currentModalName: string = useAppSelector(() => store.getState().modal.name);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCopying, setIsCopying] = useState<boolean>(false);

  const handleClose = () => {
    if (isLoading) return;
    dispatch(setModalName(''));
  };

  const handlePrimaryAction = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    if (!primaryAction?.onClick || primaryAction?.disabled || isLoading) return;

    setIsLoading(true);
    try {
      await primaryAction.onClick();
    } catch (error) {
      console.error('Primary action failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSecondaryAction = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    if (!secondaryAction?.onClick || secondaryAction?.disabled || isLoading) return;

    setIsLoading(true);
    try {
      await secondaryAction.onClick();
    } catch (error) {
      console.error('Secondary action failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!copyFeature?.text || isCopying) return;

    setIsCopying(true);
    try {
      await navigator.clipboard.writeText(copyFeature.text);
      toast.success('Copied to clipboard');
    } catch (error) {
      console.error('Copy failed:', error);
    } finally {
      setTimeout(() => setIsCopying(false), 1000);
    }
  };

  const isOpen = modalVisible && currentModalName === modalName;

  return (
    <ModalComponent
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel={title}
      contentSubtitle={subtitle}
      className={className}
      closeOnClickOff={closeOnClickOff}
      footer={
        <div className='w-full pt-4 flex flex-col gap-2'>
          <div className='flex flex-row gap-4 flex-wrap items-center w-full'>
            {!hideClose && (
              <Button outline onClick={handleClose} disabled={isLoading}>
                <CancelIcon className='w-[20px] h-[20px] mr-2' /> Close
              </Button>
            )}

            {secondaryAction && (
              <Button
                onClick={handleSecondaryAction}
                disabled={isLoading || secondaryAction.disabled}
                outline
              >
                {secondaryAction.icon}
                {secondaryAction.text}
              </Button>
            )}

            <div className='flex flex-row gap-4 flex-wrap items-center ml-auto'>
              {primaryAction && (
                <Button
                  onClick={handlePrimaryAction}
                  disabled={isLoading || primaryAction.disabled}
                >
                  {primaryAction.icon}
                  {primaryAction.text}
                </Button>
              )}

              {copyFeature && copyFeature.enabled !== false && (
                <Button onClick={handleCopy} disabled={isCopying} outline>
                  <CopyIcon className='w-[20px] h-[20px] mr-2' />
                  {isCopying ? 'Copied!' : 'Copy'}
                </Button>
              )}

              {rightAction && (
                <Button onClick={rightAction.onClick} disabled={isLoading || rightAction.disabled}>
                  {rightAction.icon}
                  {rightAction.text}
                </Button>
              )}
            </div>
          </div>
        </div>
      }
    >
      <div className='relative w-full'>
        <div className='flex flex-col gap-[16px] w-full'>
          <div className={`${bodyClassName} rounded-lg p-4 border border-border_gray_300`}>
            {children}
          </div>
        </div>

        {isLoading && (
          <div className='absolute top-0 left-0 z-10 w-full h-full flex items-center justify-center'>
            <div className='absolute inset-0 bg-surface_gray_50 opacity-70' />
            <div className='mt-3 flex flex-col items-center justify-center gap-2 text-sm z-20'>
              <LoadingSpinner />
              <span>{loadingText}</span>
            </div>
          </div>
        )}
      </div>
    </ModalComponent>
  );
}
